import { Radio as UiRadio } from "@rumbledirect/ui";
import { QuestionRadiogroupModel, ItemValue } from "survey-core";
import { useMemo } from "react";
import { createStyles } from "antd-style";
import { choiceIconRegistry } from "../registrations/choiceIconRegistry";

export class RadioLargeQuestionModel extends QuestionRadiogroupModel {
  getType() {
    return "radioLarge";
  }
}

const useStyles = createStyles(({ css, token }) => ({
  container: {
    display: "flex",
    flexDirection: "column",
    gap: "12px",
    marginTop: 15,
  },
  item: css`
    display: flex;
    align-items: center;
    border: 1px solid ${token.colorBorder};
    border-radius: 8px;
    padding: 16px 20px;
    cursor: pointer;
    transition: all 0.2s ease;
    user-select: none;

    &:hover {
      border-color: ${token.colorPrimary};
      box-shadow: 0 0 0 1px ${token.colorPrimary};
    }
  `,
  itemSelected: css`
    border-color: ${token.colorPrimary};
    background-color: ${token.colorPrimaryBg};
    box-shadow: 0 0 0 1px ${token.colorPrimary};
  `,
  itemDisabled: css`
    cursor: not-allowed;
    opacity: 0.6;
  `,
  radio: css`
    margin-right: 16px;
    pointer-events: none;

    input[type="radio"] {
      position: absolute !important;
      height: 1px !important;
      opacity: 0 !important;
    }
  `,
  icon: {
    marginRight: "16px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    color: token.colorTextSecondary,
  },
  iconSelected: {
    color: token.colorPrimary,
  },
  content: {
    flex: 1,
    fontSize: "14px",
    lineHeight: "1.5",
  },
}));

export const RadioLargeQuestion = (props: {
  question: RadioLargeQuestionModel;
}) => {
  const { styles, cx } = useStyles();
  const question = props.question;

  const selectedValue = useMemo(() => question.value, [question.value]);

  const handleItemClick = (item: ItemValue) => {
    if (question.isDisabledAttr || question.isReadOnly) return;
    question.value = item.value;
  };

  return (
    <div className={styles.container}>
      {question.visibleChoices.map((choice: ItemValue) => {
        const isSelected = selectedValue === choice.value;
        const iconKey = choice.getPropertyValue?.("icon");
        const icon = iconKey ? choiceIconRegistry.get(iconKey) : undefined;

        return (
          <div
            key={choice.value}
            className={cx(
              styles.item,
              isSelected && styles.itemSelected,
              question.isDisabledAttr && styles.itemDisabled,
            )}
            onClick={() => handleItemClick(choice)}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                handleItemClick(choice);
              }
            }}
            role="radio"
            aria-checked={isSelected}
            tabIndex={0}
          >
            <UiRadio
              checked={isSelected}
              disabled={question.isDisabledAttr}
              className={styles.radio}
              onChange={() => handleItemClick(choice)}
            />
            {icon && (
              <div
                className={cx(styles.icon, isSelected && styles.iconSelected)}
              >
                {icon}
              </div>
            )}
            <div className={styles.content}>
              <strong>{choice.text}</strong>
              {choice.getPropertyValue?.("textExtra")}
            </div>
          </div>
        );
      })}
    </div>
  );
};
